import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Download, FileText, Shield, CheckCircle, Loader2, AlertCircle, 
  ArrowLeft, Plane, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';

export const VisaDownloadPage = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [application, setApplication] = useState(null);
  const [downloading, setDownloading] = useState({ visa: false, insurance: false, all: false });
  const [downloaded, setDownloaded] = useState({ visa: false, insurance: false });

  useEffect(() => {
    fetchApplication();
  }, [applicationId]);

  const fetchApplication = async () => {
    try {
      setLoading(true);
      // Public endpoint to check application status
      const res = await api.get(`/visa-applications/${applicationId}/check-downloads`);
      setApplication(res.data);
      setDownloaded({
        visa: res.data.visa_downloaded || false,
        insurance: res.data.insurance_downloaded || false
      });
    } catch (err) {
      console.error('Error fetching application:', err);
      setError(err.response?.data?.detail || 'Application non trouvée');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (type) => {
    setDownloading(prev => ({ ...prev, [type]: true }));
    try {
      const response = await api.get(
        `/visa-applications/${applicationId}/download-visa-docs?doc_type=${type}`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = type === 'all' ? 'visa_assurance.zip' : `${type}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${type === 'visa' ? 'Visa' : type === 'insurance' ? 'Assurance' : 'Documents'} téléchargé(s) !`);
      
      // Update downloaded status
      if (type === 'all') {
        setDownloaded({ visa: true, insurance: true });
      } else {
        setDownloaded(prev => ({ ...prev, [type]: true }));
      }
      
      // Refresh status
      fetchApplication();
      
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloading(prev => ({ ...prev, [type]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#D4AF37] mx-auto" />
          <p className="mt-4 text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto" />
          <h1 className="mt-4 text-xl font-bold text-white">Erreur</h1>
          <p className="mt-2 text-gray-400">{error}</p>
          <Button onClick={() => navigate('/')} className="mt-6" variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  const isReady = application?.status === 'processed' || application?.status === 'completed';
  const isCompleted = application?.status === 'completed';

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <div className="bg-gradient-to-b from-[#0F172A] to-transparent py-8">
        <div className="max-w-xl mx-auto px-4">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          
          <div className="text-center">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center mb-4">
              <Plane className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">Vos Documents de Voyage</h1>
            <p className="mt-2 text-gray-400">Référence: {applicationId?.slice(0, 8).toUpperCase()}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-xl mx-auto px-4 pb-12">
        {isReady ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Success Message */}
            <div className="p-6 rounded-2xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 text-center">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto" />
              <h2 className="mt-4 text-xl font-semibold text-white">
                {isCompleted ? 'Documents Téléchargés' : 'Votre Visa est Prêt !'}
              </h2>
              <p className="mt-2 text-gray-400">
                {isCompleted 
                  ? 'Vous avez déjà téléchargé vos documents. Vous pouvez les télécharger à nouveau si nécessaire.'
                  : 'Téléchargez vos documents ci-dessous pour finaliser votre dossier.'
                }
              </p>
            </div>

            {/* Download Cards */}
            <div className="space-y-4">
              {/* Visa Document */}
              <div className={`p-5 rounded-xl border transition-all ${
                downloaded.visa 
                  ? 'bg-green-500/10 border-green-500/30' 
                  : 'bg-white/5 border-white/10 hover:border-[#D4AF37]/50'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      downloaded.visa ? 'bg-green-500/20' : 'bg-[#D4AF37]/20'
                    }`}>
                      {downloaded.visa ? (
                        <CheckCircle className="w-6 h-6 text-green-400" />
                      ) : (
                        <FileText className="w-6 h-6 text-[#D4AF37]" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-white">Visa Électronique</p>
                      <p className="text-sm text-gray-400">Document officiel de voyage</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleDownload('visa')}
                    disabled={downloading.visa}
                    className={downloaded.visa 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-[#D4AF37] hover:bg-[#B8860B]'
                    }
                    data-testid="download-visa"
                  >
                    {downloading.visa ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        {downloaded.visa ? 'Re-télécharger' : 'Télécharger'}
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Insurance Document */}
              <div className={`p-5 rounded-xl border transition-all ${
                downloaded.insurance 
                  ? 'bg-green-500/10 border-green-500/30' 
                  : 'bg-white/5 border-white/10 hover:border-[#D4AF37]/50'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      downloaded.insurance ? 'bg-green-500/20' : 'bg-purple-500/20'
                    }`}>
                      {downloaded.insurance ? (
                        <CheckCircle className="w-6 h-6 text-green-400" />
                      ) : (
                        <Shield className="w-6 h-6 text-purple-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-white">Assurance Voyage</p>
                      <p className="text-sm text-gray-400">Couverture complète</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleDownload('insurance')}
                    disabled={downloading.insurance}
                    className={downloaded.insurance 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-purple-600 hover:bg-purple-700'
                    }
                    data-testid="download-insurance"
                  >
                    {downloading.insurance ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        {downloaded.insurance ? 'Re-télécharger' : 'Télécharger'}
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Download All */}
              <div className="pt-4">
                <Button
                  onClick={() => handleDownload('all')}
                  disabled={downloading.all}
                  className="w-full py-6 bg-gradient-to-r from-[#D4AF37] to-[#B8860B] hover:from-[#B8860B] hover:to-[#8B6914] text-white"
                  data-testid="download-all"
                >
                  {downloading.all ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Download className="w-5 h-5 mr-2" />
                      Télécharger Tous les Documents (ZIP)
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Instructions */}
            <div className="p-4 rounded-xl bg-white/5 border border-white/10">
              <h3 className="font-medium text-white mb-2">📋 Instructions</h3>
              <ul className="text-sm text-gray-400 space-y-1">
                <li>• Imprimez votre visa électronique avant le voyage</li>
                <li>• Gardez une copie sur votre téléphone</li>
                <li>• L'assurance doit être présentée à l'entrée</li>
                <li>• Conservez ces documents pendant tout votre séjour</li>
              </ul>
            </div>

            {/* Contact */}
            <div className="text-center text-sm text-gray-500">
              <p>Des questions ? Contactez-nous</p>
              <p className="text-[#D4AF37]">+243 84 22 17 277</p>
            </div>
          </motion.div>
        ) : (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-500 mx-auto" />
            <h2 className="mt-4 text-xl font-semibold text-white">Documents en Préparation</h2>
            <p className="mt-2 text-gray-400">
              Vos documents ne sont pas encore prêts. Vous recevrez une notification WhatsApp dès qu'ils seront disponibles.
            </p>
            <p className="mt-4 text-sm text-gray-500">
              Statut actuel: <span className="text-[#D4AF37]">{application?.status || 'En cours'}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisaDownloadPage;
